import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { DataCard, styles, SdiClipboardCheck, AlertSingleLine } from '@sdir/sds';
import moment from 'moment';
import styled from 'styled-components';
import { useApi } from '@sdir/httpclient/lib/hooks/useApi';
// eslint-disable-next-line max-len
import { AssessmentsReportByPersonaIdentificationId } from '@sdir/httpclient/lib/clients/aps/assessor';
import { isToggleOn } from '@sdir/blueprint.aps/lib/helpers/permissionUtilities';
import { LoadWrapper } from '../../Atoms';
import { InfoPageLayout } from '../../Organisms';
import { DisplayDateFormat } from '../../../helpers';
import {
  AssessmentRow,
  AssessmentsHeader,
  AssessmentsHeaderMobile,
  TableDefinition,
} from '../../../helpers/TableHeaders';
import { SortableDataTable } from '../../Molecules';
import { assessmentApi } from '../../../httpclient';
import { ToggleAssessment } from '../../../constants/permissions';
import { useCurrentPerson, useFeatureToggles } from '../../../services/hooks';

export const AssessmentPage: React.FC = () => {
  const intl = useIntl();
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const featureToggle = useFeatureToggles();
  const {
    person: { result: contactInfo },
  } = useCurrentPerson();

  const userPersonalNr = useMemo(() => {
    return contactInfo?.personalIdentificationNumber || '';
  }, [contactInfo]);

  const showAssessment = !isToggleOn(featureToggle?.result, ToggleAssessment, userPersonalNr);

  const {
    result: assessments,
    error,
    loading,
  } = useApi({
    apiFunction: () => {
      const query = {
        take: 100,
        skip: 0,
      };
      return assessmentApi.v1AssessmentAssessmentsbyidentificationidQueryPost(query);
    },
    callApiOnInit: true,
  });

  const tableMobile = useMemo<TableDefinition<AssessmentRow> | null>(() => {
    if (!assessments) return null;
    return {
      columns: AssessmentsHeaderMobile(intl.formatMessage),
      data: assessments.map((e) => ({
        completedDate: DisplayDateFormat(e.completionDate),
        completedDateValue: moment(e.completionDate),
        vessel: e.vesselName || '',
        vesselValue: e.vesselName || '',
        competence: e.assessmentType || '',
        assessor: e.assessorName || '',
        period: `${e.signOnDate ? DisplayDateFormat(e.signOnDate) : ''} - ${
          e.signOffDate ? DisplayDateFormat(e.signOffDate) : ''
        }`,
        periodStart: moment(e.signOnDate),
        periodEnd: moment(e.signOffDate),
        childContent: <RowMobileContent rowData={e} fm={intl.formatMessage} />,
      })),
      sortableKeys: ['competence'],
      collapsable: true,
      collapsableText: [
        intl.formatMessage({ id: 'general.table.viewdetails' }),
        intl.formatMessage({ id: 'general.table.hidedetails' }),
      ],
    };
  }, [assessments, intl]);

  const table = useMemo<TableDefinition<AssessmentRow> | null>(() => {
    if (!assessments) return null;

    // const loadVesselForAssessment = (assessment: AssessmentsReportByPersonaIdentificationId) => {
    //   if (!assessment.vesselId)
    //     return Promise.reject(
    //       new Error(intl.formatMessage({ id: 'vessel.preview.errors.missingvesselid' }))
    //     );
    //   return vesselApi
    //     .v1VesselSearchfromrouteUidGet(assessment.vesselId)
    //     .then((r) => r.data?.source?.[0] || {});
    // };

    return {
      columns: AssessmentsHeader(intl.formatMessage),
      data: assessments.map((e) => ({
        completedDate: DisplayDateFormat(e.completionDate),
        completedDateValue: moment(e.completionDate),
        vessel: e.vesselName || '',
        // e.vesselId ? (
        //   <VesselPreviewLink
        //     name={e.vesselName || ''}
        //     loadAction={() => loadVesselForAssessment(e)}
        //   />
        // ) : (
        //   e.vesselName || ''
        // ),
        vesselValue: e.vesselName || '',
        competence: e.assessmentType || '',
        assessor: e.assessorName || '',
        period: `${e.signOnDate ? DisplayDateFormat(e.signOnDate) : ''} - ${
          e.signOffDate ? DisplayDateFormat(e.signOffDate) : ''
        }`,
        periodStart: moment(e.signOnDate),
        periodEnd: moment(e.signOffDate),
      })),
      sortableKeys: '*',
    };
  }, [assessments]);

  useEffect(() => {
    function handleResize() {
      const isMobileScreen = !!(window?.innerWidth < 768);
      setIsMobile(isMobileScreen);
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  if (!showAssessment) {
    return (
      <AlertSingleLine
        title="Error"
        type="error"
        text={intl.formatMessage({ id: 'utils.errors.insufficientaccess' })}
      />
    );
  }

  return (
    <InfoPageLayout
      title={intl.formatMessage({ id: 'home.tile.assessment' })}
      description={
        <FormattedMessage
          id="assessment.header.description"
          values={{
            br: () => <br />,
            link: (
              <a href="https://kompetanse.sdir.no/" target="_blank" rel="noreferrer">
                https://kompetanse.sdir.no/
              </a>
            ),
          }}
        />
      }
      icon={<SdiClipboardCheck color={styles.colors.secondary.seaBlue4} size="l" />}
    >
      <DataCard title={intl.formatMessage({ id: 'home.tile.assessment' })}>
        <LoadWrapper loading={loading} error={error} errorTitleId="assessment.table.error">
          {table && tableMobile && (
            <TableWrapper data-testid="assessment-table">
              <SortableDataTable table={isMobile ? tableMobile : table} autoWidth />
            </TableWrapper>
          )}
        </LoadWrapper>
      </DataCard>
    </InfoPageLayout>
  );
};

interface RowMobileContentProps {
  rowData: AssessmentsReportByPersonaIdentificationId;
  fm: any;
}

const RowMobileContent: React.FC<RowMobileContentProps> = ({ rowData, fm }) => {
  return (
    <>
      <MobileContentItem>
        <SubHeader>{fm({ id: 'assessment.table.subheader1' })}</SubHeader>
        <SubValue>{DisplayDateFormat(rowData.completionDate)}</SubValue>
      </MobileContentItem>
      <MobileContentItem>
        <SubHeader>{fm({ id: 'assessment.table.subheader2' })}</SubHeader>
        <SubValue>{rowData.assessorName || ''}</SubValue>
      </MobileContentItem>
      <MobileContentItem>
        <SubHeader>{fm({ id: 'common.table.vessel' })}</SubHeader>
        <SubValue>{rowData.vesselName || ''}</SubValue>
      </MobileContentItem>
      <MobileContentItem>
        <SubHeader>{fm({ id: 'common.table.periode' })}</SubHeader>
        <SubValue>
          {`${rowData.signOnDate ? DisplayDateFormat(rowData.signOnDate) : ''} - ${
            rowData.signOffDate ? DisplayDateFormat(rowData.signOffDate) : ''
          }`}
        </SubValue>
      </MobileContentItem>
    </>
  );
};

const TableWrapper = styled.div`
  max-width: 100%;
  overflow: auto;
`;

const MobileContentItem = styled.div`
  margin-bottom: 10px;
`;

const SubHeader = styled.span`
  display: block;
  font-weight: bold;
  margin-bottom: 2px;
`;

const SubValue = styled.span`
  display: block;
`;
